import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import './reviews.css';

const ETSY_REVIEWS = 'https://www.etsy.com/shop/MyRedlandsFarm#reviews';
const importedReviews = [
  { name: 'Naedia', rating: 5, text: "They all taste great. I've ordered from here multiple times.", source: 'Customer review supplied by My Redlands Farm' },
  { name: 'MarAnn Thornhill', rating: 5, text: 'Got my fruits for my 3rd purchase as well as the replacement.', source: 'Customer review from our previous website' },
  { name: 'Nicole Edwards', rating: 5, text: 'The fruit was good quality and came in earlier than expected! Thank you!', source: 'Customer review from our previous website' },
];
const fallback = { reviews: importedReviews, mode: 'imported', updatedAt: null };
const MAX_REVIEW_AGE = 24 * 60 * 60 * 1000;
const REVALIDATE_INTERVAL = 15 * 60 * 1000;
let pendingRequest;

function timestamp(value) {
  if (value === null || value === undefined || value === '') return null;
  const number = typeof value === 'number' || /^\d+(\.\d+)?$/.test(String(value)) ? Number(value) : NaN;
  const result = Number.isFinite(number) ? (number < 1e12 ? number * 1000 : number) : Date.parse(value);
  return Number.isFinite(result) && result > 0 ? result : null;
}

function unexpired(data) {
  if (data.mode !== 'etsy') return true;
  const refreshed = timestamp(data.updatedAt);
  const now = Date.now();
  return refreshed !== null && refreshed <= now + 60000 && now - refreshed < MAX_REVIEW_AGE;
}

function requestReviews() {
  // Share an in-flight request between the page and popup, but revalidate on later mounts.
  if (pendingRequest) return pendingRequest;
  pendingRequest = (async () => {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 12000);
    try {
      const response = await fetch('/api/site/reviews', { signal: controller.signal, headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Review service unavailable');
      const data = await response.json();
      const reviews = Array.isArray(data.reviews) ? data.reviews.filter(r => r && typeof r.text === 'string' && r.text.trim() && r.text.length <= 6000 && Number.isInteger(r.rating) && r.rating >= 1 && r.rating <= 5).map(r => ({
        name: typeof r.name === 'string' ? r.name.slice(0, 150) : 'Customer',
        rating: r.rating,
        text: r.text,
        source: typeof r.source === 'string' ? r.source : '',
        date: timestamp(r.date),
      })) : [];
      if (!reviews.length || !['etsy', 'imported'].includes(data.mode)) return fallback;
      const result = { reviews, mode: data.mode, updatedAt: timestamp(data.updatedAt), stale: Boolean(data.stale) };
      return unexpired(result) ? result : fallback;
    } catch {
      return fallback;
    } finally {
      window.clearTimeout(timeout);
      pendingRequest = undefined;
    }
  })();
  return pendingRequest;
}

function useReviews() {
  const [data, setData] = useState(fallback);
  useEffect(() => {
    let active = true;
    let expiry;
    let current = fallback;
    function expireIfNeeded() {
      if (!unexpired(current)) {
        current = fallback;
        setData(fallback);
      }
    }
    function refresh() {
      if (!active) return;
      expireIfNeeded();
      requestReviews().then(result => {
        if (!active) return;
        window.clearTimeout(expiry);
        current = unexpired(result) ? result : fallback;
        setData(current);
        if (current.mode === 'etsy') {
          expiry = window.setTimeout(() => {
            if (!active) return;
            current = fallback;
            setData(fallback);
            refresh();
          }, Math.max(1, timestamp(current.updatedAt) + MAX_REVIEW_AGE - Date.now()));
        }
      });
    }
    const visibility = () => { if (!document.hidden) refresh(); };
    refresh();
    const timer = window.setInterval(refresh, REVALIDATE_INTERVAL);
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', visibility);
    return () => {
      active = false;
      window.clearInterval(timer);
      window.clearTimeout(expiry);
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', visibility);
    };
  }, []);
  return unexpired(data) ? data : fallback;
}

function formattedDate(value) {
  if (!value) return '';
  const date = new Date(timestamp(value) || NaN);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function originText(data, compact = false) {
  if (data.mode !== 'etsy') return compact ? 'Imported customer review · not a live Etsy feed' : 'Selected imported customer reviews supplied by My Redlands Farm. These are not a live Etsy feed';
  const refreshed = formattedDate(data.updatedAt);
  const date = refreshed ? ` · refreshed ${refreshed}` : '';
  if (data.stale) return `Cached Etsy reviews${date} · refresh currently unavailable`;
  return `Etsy API reviews${date}`;
}

function Stars({ rating }) {
  return <div className="review-stars" aria-label={`${rating} out of 5 stars`}>{'★'.repeat(rating)}<span aria-hidden="true">{'☆'.repeat(5 - rating)}</span></div>;
}

function EtsyDisclaimer() {
  return <p className="source-note">The term 'Etsy' is a trademark of Etsy, Inc. This Application uses Etsy's API, but is not endorsed or certified by Etsy.</p>;
}

export function ReviewsContent() {
  const data = useReviews();
  return <main id="main" className="page-main wrap">
    <div className="eyebrow gold">WORDS FROM OUR CUSTOMERS</div>
    <h1>A little fruit.<br /><em>A lasting impression.</em></h1>
    <div className="review-intro">
      <p className="page-lead">Thank you for making our fruit part of your table.</p>
      <a className="line-link" href={ETSY_REVIEWS} target="_blank" rel="noopener noreferrer">See current ratings &amp; reviews on Etsy</a>
      <p className="review-origin">{originText(data)}. Visit Etsy for the current overall rating and review count.</p>
      <EtsyDisclaimer />
    </div>
    <div id="review-list">
      {data.reviews.map((review, index) => <article className="review-card" key={`${review.name}-${index}`}>
        <Stars rating={review.rating} />
        <blockquote>{review.text}</blockquote>
        <p>{review.name}</p>
        <p className="source-note">{data.mode === 'etsy' ? 'Etsy customer review' : review.source || 'Imported customer review'}{formattedDate(review.date) ? ` · ${formattedDate(review.date)}` : ''}</p>
      </article>)}
    </div>
    <div className="blog-nav"><Link to="/share/">Share your own story</Link><a href="https://www.etsy.com/shop/MyRedlandsFarm" target="_blank" rel="noopener noreferrer">Visit the Etsy shop</a></div>
  </main>;
}

export function ReviewPopup() {
  const data = useReviews();
  const [index, setIndex] = useState(0);
  const [dismissed, setDismissed] = useState(() => { try { return sessionStorage.getItem('jp-review-dismissed') === '1'; } catch { return false; } });
  const [visible, setVisible] = useState(false);
  const [paused, setPaused] = useState(() => window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [hidden, setHidden] = useState(document.hidden);
  const [reduced, setReduced] = useState(() => window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  const [transitioning, setTransitioning] = useState(false);
  const box = useRef(null);
  const animation = useRef(null);
  const advancing = useRef(false);
  const manualAdvance = useRef(false);
  const mounted = useRef(true);
  const pausedRef = useRef(false);
  const cyclePaused = paused || hovered || focused || hidden;
  pausedRef.current = cyclePaused;

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; animation.current?.cancel(); };
  }, []);

  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const change = () => {
      setReduced(media.matches);
      if (media.matches) {
        setPaused(true);
        animation.current?.cancel();
      }
    };
    const visibility = () => setHidden(document.hidden);
    if (media.addEventListener) media.addEventListener('change', change);
    else media.addListener(change);
    document.addEventListener('visibilitychange', visibility);
    return () => {
      if (media.removeEventListener) media.removeEventListener('change', change);
      else media.removeListener(change);
      document.removeEventListener('visibilitychange', visibility);
    };
  }, []);

  useEffect(() => {
    if (dismissed) return;
    const timer = window.setTimeout(() => setVisible(true), 2500);
    return () => window.clearTimeout(timer);
  }, [dismissed]);

  useEffect(() => {
    if (!animation.current || manualAdvance.current) return;
    if (cyclePaused) animation.current.pause();
    else animation.current.play();
  }, [cyclePaused]);

  const advance = useCallback(async (manual = false) => {
    if (advancing.current || !box.current || data.reviews.length < 2) return;
    advancing.current = true;
    manualAdvance.current = manual;
    setTransitioning(true);
    try {
      const animate = async frames => {
        const current = box.current.animate(frames, { duration: 2000, easing: 'ease-in-out', fill: 'forwards' });
        animation.current = current;
        if (!manual && pausedRef.current) current.pause();
        await current.finished;
        return current;
      };
      if (!reduced && box.current.animate) await animate([{ opacity: 1 }, { opacity: 0 }]);
      if (!mounted.current) return;
      setIndex(previous => (previous + 1) % data.reviews.length);
      if (!reduced && box.current.animate) {
        // The next review changes while the entire translucent window is invisible.
        const previous = animation.current;
        const reveal = animate([{ opacity: 0, offset: 0 }, { opacity: 0, offset: 0.15 }, { opacity: 1, offset: 1 }]);
        previous?.cancel();
        await reveal;
      }
    } catch {
      // Cancelling on close or unmount is expected.
    } finally {
      animation.current?.cancel();
      animation.current = null;
      advancing.current = false;
      manualAdvance.current = false;
      if (mounted.current) setTransitioning(false);
    }
  }, [data.reviews.length, reduced]);

  useEffect(() => {
    if (!visible || dismissed) return;
    const timer = window.setInterval(() => { if (!pausedRef.current) advance(); }, 12000);
    return () => window.clearInterval(timer);
  }, [advance, visible, dismissed]);

  function dismiss() {
    animation.current?.cancel();
    setDismissed(true);
    try { sessionStorage.setItem('jp-review-dismissed', '1'); } catch {}
  }

  if (!visible || dismissed) return null;
  const review = data.reviews[index % data.reviews.length];
  return <aside ref={box} className="review-popup quiet-review-popup" aria-label="Customer reviews"
    onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
    onFocus={() => setFocused(true)} onBlur={event => { if (!event.currentTarget.contains(event.relatedTarget)) setFocused(false); }}>
    <button type="button" className="review-close" aria-label="Dismiss customer reviews" onClick={dismiss}>×</button>
    <div className="review-slide">
      <div className="review-top"><img src="/assets/june-plums-tree.jpeg" alt="June plums" /><div><Stars rating={review.rating} /><div className="review-meta">{review.name}</div></div></div>
      <blockquote>{review.text}</blockquote>
      <p className="review-popup-origin">{originText(data, true)}</p>
    </div>
    <div className="review-controls"><Link to="/reviews/">Read reviews</Link><button type="button" aria-pressed={paused} onClick={() => setPaused(value => !value)}>{paused ? 'Play' : 'Pause'}</button><button type="button" disabled={transitioning} onClick={() => advance(true)}>Next</button></div>
    {data.mode === 'etsy' && <div className="review-popup-attribution"><a href={ETSY_REVIEWS} target="_blank" rel="noopener noreferrer">View current reviews on Etsy</a><EtsyDisclaimer /></div>}
  </aside>;
}
